import { useSubscriptionSummary } from '../../hooks/useSubscriptionSummary';
import { keyScopeSpend } from '../../hooks/useAccountModels';

/**
 * Settings › Account › Models — This period (task 1147, step 8).
 *
 * Model spend split by who paid the provider, from the billing subscription
 * summary's `usage` (`GET /billing/subscription`, the owner-only proxy of the
 * AMS read; AMS task 1148 added `usage.accountKeyCost` / `platformKeyCost` as
 * micro-USD strings). AMS always sends `usage`, "0" for a month with no usage,
 * so a $0.00 here is a real zero. When the summary cannot be read (a non-owner
 * admin is refused, or the read is unavailable) the card says so instead of
 * showing figures it does not have. Spend on the account's own keys is at
 * catalog list prices, for information: the real bill comes from the provider.
 */

const usd = (dollars: number) => `$${dollars.toFixed(2)}`;

export function ThisPeriodSection() {
  const { data: summary, isLoading } = useSubscriptionSummary();
  const spend = keyScopeSpend(summary);
  const figure = (dollars: number | undefined) => (isLoading ? '…' : dollars === undefined ? '—' : usd(dollars));

  return (
    <section
      data-testid="account-models-this-period"
      className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800"
    >
      <h2 className="text-base font-semibold text-gray-900 dark:text-white">This period</h2>
      <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
        Model spend by who paid the provider. Spend on your keys is shown for information and is not on your Innovate
        invoice.
      </p>

      <dl className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="rounded-lg border border-gray-200 p-3 dark:border-gray-700">
          <dt className="text-xs text-gray-500 dark:text-gray-400">
            Spend on your keys (informational, paid to provider)
          </dt>
          <dd
            className="mt-1 text-xl font-semibold tabular-nums text-gray-900 dark:text-white"
            data-testid="this-period-account-key-spend"
          >
            {figure(spend?.accountKeyDollars)}
          </dd>
        </div>
        <div className="rounded-lg border border-gray-200 p-3 dark:border-gray-700">
          <dt className="text-xs text-gray-500 dark:text-gray-400">
            Spend on platform key (billed on Innovate invoice)
          </dt>
          <dd
            className="mt-1 text-xl font-semibold tabular-nums text-gray-900 dark:text-white"
            data-testid="this-period-platform-key-spend"
          >
            {figure(spend?.platformKeyDollars)}
          </dd>
        </div>
      </dl>

      {!isLoading && !spend && (
        <p className="mt-3 text-xs text-gray-500 dark:text-gray-400" data-testid="this-period-split-unavailable">
          Spend by key comes from the account&apos;s billing summary, which only the account owner can read.
        </p>
      )}
    </section>
  );
}
